import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { loadAbsences, deleteAbsence } from "./patientAbsencesSlice";
import { Modal, Table, Typography, Button } from "antd";
import Surface from "../../components/layouts/Surface";
import AddUpdateAbsence from "./components/AddUpdateAbsence";
import Date from "../../components/tables/Date";
import AbsenceReason from "../../components/tables/AbsenceReason";
import FormHeaderWrapper from "../../components/forms/FormHeader.style";
import { useLocation } from "react-router-dom";
import { loadRecord } from "../patientDetails/patientDetailsSlice";

import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
const { Title } = Typography;

const PatientAbsences = (props) => {
  const { nonActive } = props;
  const dispatch = useDispatch();
  let location = useLocation();
  const [modal, contextHolder] = Modal.useModal();
  const [visible, setVisible] = useState(false);
  const [addUpdateAbsenceVisible, setAddUpdateAbsenceVisible] = useState(false);
  const [mode, setMode] = useState(null);
  const [activeRecord, setActiveRecord] = useState(null);
  const items = useSelector((state) => state.patientAbsences.absences);
  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );
  const loading = useSelector(
    (state) => state.pendingActions["patientAbsences/load"]
  );
  const deleteLoading = useSelector(
    (state) => state.pendingActions["patientAbsences/delete"]
  );
  const intl = useIntl();

  useEffect(() => {
    if (activePatient.id) {
      dispatch(loadAbsences(activePatient.id));
    }
  }, [dispatch, activePatient.id]);

  const onEdit = (record) => {
    setActiveRecord(record);
    setMode("edit");
    setAddUpdateAbsenceVisible(true);
    setVisible(true);
  };

  const onAdd = (record) => {
    setMode("new");
    setAddUpdateAbsenceVisible(true);
    setVisible(true);
  };

  const onDelete = async (record) => {
    const title = intl.formatMessage({
      id: "app.absences.deleteTitle",
    });
    const content = intl.formatMessage({
      id: "app.absences.deleteContent",
    });
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return deleteOrder(record);
      },
      onCancel() {},
    });
  };

  const deleteOrder = async (record) => {
    setActiveRecord(record);
    const params = {
      branchId: activePatient.branch,
      patientId: activePatient.id,
      absenceId: record.id,
    };
    const action = await dispatch(deleteAbsence(params));
    if (!action.error) {
      setActiveRecord(null);
      dispatch(loadAbsences(activePatient.id));
      getPatient(activePatient.id);
    }
  };

  const hide = () => {
    setVisible(false);
    dispatch(loadAbsences(activePatient.id));
    setAddUpdateAbsenceVisible(false);
    setActiveRecord(null);
  };

  const getPatient = (id) => {
    const isAdmin = location.pathname.includes("admin");
    const params = {
      id: id,
      branchId: activePatient.branch,
      admin: isAdmin,
    };
    dispatch(loadRecord(params));
  };

  return (
    <Surface>
      {contextHolder}
      <FormHeaderWrapper>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            <FormattedMessage id={`app.absences.title`} />
          </Title>
          <Button type="primary" onClick={onAdd} disabled={nonActive}>
            <FormattedMessage id={`app.absences.addAbsence`} />
          </Button>
        </div>
      </FormHeaderWrapper>

      <Table
        bordered
        rowKey={(record) => record.id}
        scroll={{ y: window.innerHeight - 526 }}
        dataSource={items}
        pagination={false}
        columns={[
          {
            title: intl.formatMessage({
              id: "app.absences.effective_from",
            }),
            dataIndex: "effective_from",

            type: "date",
            required: true,
            render(text, record) {
              return <Date text={text} format={"L"} />;
            },
            sorter: (a, b) =>
              a["effective_from"].localeCompare(b["effective_from"]),
            sortDirections: ["descend", "ascend"],
            defaultSortOrder: "descend",
          },
          {
            title: intl.formatMessage({
              id: "app.absences.effective_to",
            }),
            dataIndex: "effective_to",

            type: "date",
            required: true,
            render(text, record) {
              return <Date text={text} format={"L"} />;
            },
          },
          {
            title: intl.formatMessage({
              id: "app.absences.reason_id",
            }),
            dataIndex: "reason_id",

            type: "settingsDataItem",
            render(text, record) {
              return (
                <AbsenceReason
                  record={record}
                  text={text}
                  settingsataName={"system_tables/absence_reasons"}
                />
              );
            },
          },
          {
            title: intl.formatMessage({
              id: "app.absences.comment",
            }),
            dataIndex: "comment",
            ellipsis: true,
          },
          {
            title: intl.formatMessage({
              id: "app.absences.actions",
            }),
            width: "150px",
            render(text, record) {
              return (
                <div>
                  <Button
                    type="link"
                    loading={deleteLoading && activeRecord.id === record.id}
                    onClick={() => {
                      onDelete(record);
                    }}
                  >
                    <DeleteOutlined style={{ color: "red" }} />
                  </Button>
                  <Button
                    type="link"
                    onClick={() => {
                      onEdit(record);
                    }}
                  >
                    <EditOutlined style={{}} />
                  </Button>
                </div>
              );
            },
          },
        ]}
        loading={loading}
      />
      <Modal
        closable={false}
        maskClosable={false}
        open={visible}
        footer={null}
        onCancel={hide}
        title={null}
        width={"fit-content"}
        styles={{
          body: {
            padding: 0,
          },
        }}
        destroyOnClose={true}
      >
        {addUpdateAbsenceVisible ? (
          <AddUpdateAbsence
            activePatient={activePatient}
            hide={hide}
            mode={mode}
            record={activeRecord}
            getPatient={getPatient}
            addText={
              mode === "edit" ? (
                <FormattedMessage id="app.absences.editAbsence" />
              ) : (
                <FormattedMessage id="app.absences.addNewAbsence" />
              )
            }
          />
        ) : null}
      </Modal>
    </Surface>
  );
};
export default PatientAbsences;
